// useAuthContextRecovery (SIM-7 / ACS-4131, AC-AUTH-REFRESH): survives a page
// refresh on the Authorization (method-choice) step so the user is re-rendered on
// the Authorization step — NOT bounced back to credential entry — with the same
// resolved-account context, WITHOUT a new lookup or OTP send (BR-021).
//
// This is a SIBLING of useOtpSessionRecovery (SIM-10), deliberately mirroring its
// whitelist / SSR-safety / try-catch conventions. It differs in ONE way: it has NO
// mount effect and never calls goTo. Navigation precedence (OTP > authorization >
// credential) is orchestrated in ONE place — SignIn.f.jsx — so the two recovery
// mechanisms compose instead of racing each other's mount effects.
//
// PII (BR-021): the AuthorizationScreen displays the RAW enteredCredential the user
// typed, but that raw value is PII and MUST NOT be persisted. Only the non-PII,
// recoverable subset below is stored; on refresh-restore AuthorizationScreen falls
// back to the MASKED credential (maskedEmail / `(***) ***-{phoneLast4Digits}`),
// which ARE persistable. securityToken is intentionally EXCLUDED to follow the
// SIM-10 precedent (useOtpSessionRecovery persists neither securityToken nor any
// token). Consequence: a refresh-restored authorization context has no
// securityToken; selecting a method will seed/send OTP as normal (the send seam in
// SignIn.f.jsx / OtpEntryScreen re-derives what it needs), so no privileged token
// is smuggled through sessionStorage.
import { useCallback } from 'react';

const STORAGE_KEY = 'auth_context_state';
// The ONLY fields persisted — recoverable, non-PII. The raw enteredCredential,
// and securityToken (per SIM-10 precedent), are intentionally dropped.
const PERSIST_FIELDS = ['availableAuthMethods', 'phoneLast4Digits', 'maskedEmail', 'credentialType'];

/**
 * Pure persist/recover/clear for the authorization-step context. No mount effect,
 * no navigation — SignIn.f.jsx owns the precedence + goTo decision.
 * @returns {{ persist: Function, recover: Function, clear: Function }}
 */
const useAuthContextRecovery = () => {
    const recover = useCallback(() => {
        try {
            const raw = window.sessionStorage.getItem(STORAGE_KEY);

            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            // Storage unavailable (private mode) — treat as no recoverable state.
            return null;
        }
    }, []);

    const persist = useCallback(state => {
        const subset = {};

        PERSIST_FIELDS.forEach(field => {
            if (state && state[field] !== undefined) {
                subset[field] = state[field];
            }
        });

        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(subset));
        } catch (e) {
            // Storage unavailable — persistence is best-effort; do not throw.
        }
    }, []);

    const clear = useCallback(() => {
        try {
            window.sessionStorage.removeItem(STORAGE_KEY);
        } catch (e) {
            // Storage unavailable — nothing to clear; do not throw.
        }
    }, []);

    return { persist, recover, clear };
};

export default useAuthContextRecovery;

export {
    STORAGE_KEY as AUTH_CONTEXT_STORAGE_KEY, PERSIST_FIELDS as AUTH_CONTEXT_PERSIST_FIELDS
};
